/* Confirmation for a square being dragged somewhere else — to another
 * technician, or up and down one technician's own column.
 *
 * Two different questions wear the same sheet, and which one it is depends on
 * whether the service is still running.
 *
 * A RUNNING service is a client in a chair. The real decision is the timer: a
 * client moving chairs mid-service usually keeps their elapsed time, and a
 * restart means the service genuinely began again. That is why there are two
 * buttons and not one.
 *
 * A FINISHED one is a record of work already done, and there is nothing to
 * decide about its clock — the times are what happened. What matters instead is
 * what the correction runs over, because a record can be moved onto somebody
 * who was doing something else at the time, into the middle of a column that
 * re-forms around it, or onto a name the booking system disagrees with. Every
 * one of those is on the sheet, and any of them turns the button from "Move" to
 * "Move anyway".
 *
 * Nothing here works anything out. `store.moveConfirmDetail` re-reads the board
 * on every paint — a sheet can sit open across a webhook, an auto-stop or
 * another station's drag, and a sheet describing a board that has moved on is a
 * sheet that gets said yes to.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const MoveConfirm = {
  name: 'MoveConfirm',
  setup: function () {
    var d = store.moveConfirmDetail;

    var title = Vue.computed(function () {
      if (!d.value) return '';
      if (d.value.same) return 'Move this square up the column?';
      return d.value.running ? 'Move this service?' : 'Move this finished square?';
    });

    // Where it is going, in one line. A reorder inside one column is not a
    // handover, and "Ana → Ana" would read as a bug rather than as a move.
    var route = Vue.computed(function () {
      if (!d.value) return '';
      return d.value.same
        ? d.value.label + ' · ' + d.value.to
        : d.value.label + ' · ' + d.value.from + ' → ' + d.value.to;
    });

    var lands = Vue.computed(function () {
      if (!d.value) return '';
      var at = 'Turn ' + d.value.at;
      if (!d.value.below) return at;
      return at + ' — ' + d.value.below +
        (d.value.below === 1 ? ' square below it moves down' : ' squares below it move down');
    });

    // "4 → 5 turns", so the number that decides who gets the next walk-in is
    // read as a change rather than as a total.
    function creditLine(side) {
      return store.formatCredit(side.was) + ' → ' + store.pluralTurns(side.now);
    }

    var go = Vue.computed(function () {
      if (!d.value) return 'Move';
      return d.value.conflicts.length ? 'Move anyway' : 'Move the square';
    });

    return {
      d: d,
      title: title,
      route: route,
      lands: lands,
      creditLine: creditLine,
      go: go,
      close: store.closeMoveConfirm,
      confirmMove: store.confirmMove
    };
  },
  template: [
    '<div v-if="d" class="modal-scrim" @click.self="close">',
    '  <div class="sheet" role="dialog" aria-modal="true">',
    '    <div class="sheet__head" :style="{ \'--svc\': d.color }">',
    '      <span class="sheet__letter">{{ d.letter }}</span>',
    '      <div>',
    '        <h3>{{ title }}</h3>',
    '        <p>{{ route }}</p>',
    '      </div>',
    '    </div>',
    '',
    /* The board has moved on underneath the sheet — somebody else took the
       column, or the technician was marked out. Said rather than left to be
       discovered by a button that fails when it is pressed. */
    '    <p v-if="d.blocked" class="sheet__blocked">{{ d.blocked }}</p>',
    '',
    '    <dl class="sheet__facts">',
    '      <div><dt>Lands on</dt><dd>{{ lands }}</dd></div>',
    '      <div v-if="d.running"><dt>Running for</dt><dd class="sheet__elapsed">{{ d.elapsed }}</dd></div>',
    /* A finished square carries its own times on it, and they are the whole
       reason it can be moved at all: they are what says which service this was
       and when it happened. */
    '      <div v-else><dt>Ran</dt><dd>{{ d.started }} &ndash; {{ d.ended }} ({{ d.elapsed }})</dd></div>',
    '      <div v-if="d.credit && d.credit.from"><dt>{{ d.credit.from.name }}</dt>',
    '        <dd>{{ creditLine(d.credit.from) }}</dd></div>',
    '      <div v-if="d.credit"><dt>{{ d.credit.to.name }}</dt>',
    '        <dd>{{ creditLine(d.credit.to) }}</dd></div>',
    '    </dl>',
    '',
    /* What the move runs over. Every line here is something the board already
       believed that this contradicts, which is why the button below changes
       its word when there is one. */
    '    <ul v-if="d.conflicts.length" class="sheet__conflicts">',
    '      <li v-for="c in d.conflicts" :key="c.key">',
    '        <span class="sheet__conflict-mark" aria-hidden="true">&#9888;</span>',
    '        <span>{{ c.text }}</span>',
    '      </li>',
    '    </ul>',
    '',
    '    <ul v-if="d.notes.length" class="sheet__notes">',
    '      <li v-for="n in d.notes" :key="n">',
    '        <span class="sheet__note-mark" aria-hidden="true">&#10003;</span>',
    '        <span>{{ n }}</span>',
    '      </li>',
    '    </ul>',
    '',
    '    <div class="sheet__actions">',
    /* A running service is a client in a chair, and the only question worth a
       button is what happens to their clock. */
    '      <template v-if="d.running && !d.blocked">',
    '        <button type="button" class="btn btn--primary btn--lg" @click="confirmMove(false)">',
    '          Move &amp; keep the timer running</button>',
    '        <button type="button" class="btn btn--ghost" @click="confirmMove(true)">',
    '          Move &amp; reset the timer to 0:00</button>',
    '      </template>',
    /* A finished square has both its times on it already and they are the
       record, so there is nothing to ask and nothing here touches them. */
    '      <button v-else-if="!d.blocked" type="button" class="btn btn--lg"',
    '        :class="d.conflicts.length ? \'btn--danger\' : \'btn--primary\'"',
    '        @click="confirmMove(false)">{{ go }}</button>',
    '      <button type="button" class="btn btn--quiet" @click="close">Cancel</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
